import React from "react";
import { cn } from "../../utils/cn";

/**
 * AuroraBackground
 * Animated aurora borealis background layer.
 * Renders an animated gradient overlay as an absolute-positioned div.
 *
 * Props:
 *   className          – extra classes on the outer wrapper
 *   children           – content rendered on top of the aurora
 *   showRadialGradient – apply an elliptical mask so the aurora fades
 *                        toward the bottom-left (default: true)
 */
export const AuroraBackground = ({
  className,
  children,
  showRadialGradient = true,
  ...props
}) => {
  return (
    <div
      className={cn(
        "relative flex flex-col h-full w-full items-center justify-center",
        className
      )}
      {...props}
    >
      {/* Aurora layer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={cn(
            // Dark gradient base (blends with the dark navy background)
            "[--dark-gradient:repeating-linear-gradient(100deg,var(--black)_0%,var(--black)_7%,var(--transparent)_10%,var(--transparent)_12%,var(--black)_16%)]",
            // Aurora color bands: blue → indigo → blue-light → violet → blue
            "[--aurora:repeating-linear-gradient(100deg,var(--blue-500)_10%,var(--indigo-300)_15%,var(--blue-300)_20%,var(--violet-200)_25%,var(--blue-400)_30%)]",
            // Combine gradients and size them large so animation has room to travel
            "[background-image:var(--dark-gradient),var(--aurora)]",
            "[background-size:300%,_200%]",
            "[background-position:50%_50%,50%_50%]",
            // Soft blur + no invert (dark theme)
            "filter blur-[10px]",
            // Animated ::after layer for the shimmer / blend effect
            'after:content-[""]',
            "after:absolute after:inset-0",
            "after:[background-image:var(--dark-gradient),var(--aurora)]",
            "after:[background-size:200%,_100%]",
            "after:animate-aurora",
            "after:[background-attachment:fixed]",
            "after:mix-blend-difference",
            // Positioning
            "absolute -inset-[10px] opacity-50 will-change-transform",
            // Optional radial mask — aurora appears top-right, fades bottom-left
            showRadialGradient &&
              "[mask-image:radial-gradient(ellipse_at_80%_0%,black_15%,var(--transparent)_72%)]"
          )}
        />
      </div>

      {/* Content */}
      {children}
    </div>
  );
};

export default AuroraBackground;
